import { QueryFunctionContext } from '@tanstack/react-query';

import { JOBS_PAGE_SIZE } from '@/constant';
import api, { handleError } from '@/services/axiosInstance';
import type {
  JobData,
  JobHistoryRequest,
  JobList,
  JobListType,
  WorksheetResponse,
} from '@/types/jobs';
import { normalizeIds } from '@/utils/normalizeIds';

const API_BASE_URL = 'https://api.vh3connect.io/api:uCamsOiN/jobs';
const JOB_DETAIL_V3_BASE_URL = 'https://api.vh3connect.io/api:f-Fs4_ed/jobs';

const normalizeResponse = <T>(data: T | undefined): T | undefined =>
  data ? normalizeIds(data) : data;

export const jobsApi = {
  listActiveJobs: async (
    {
      pageParam = 1,
    }: QueryFunctionContext<[string, JobListType], number> = {} as any
  ): Promise<JobList | undefined> => {
    try {
      const { data } = await api.get<JobList>('/jobFeed', {
        baseURL: API_BASE_URL,
        params: {
          pageNumber: pageParam,
          pageSize: JOBS_PAGE_SIZE,
        },
      });
      return normalizeResponse(data);
    } catch (error) {
      handleError(error, 'listActiveJobs');
    }
  },

  listScheduledJobs: async (
    {
      pageParam = 1,
    }: QueryFunctionContext<[string, JobListType], number> = {} as any
  ): Promise<JobList | undefined> => {
    try {
      const { data } = await api.get<JobList>('/scheduledFeed', {
        baseURL: API_BASE_URL,
        params: {
          pageNumber: pageParam,
          pageSize: JOBS_PAGE_SIZE,
        },
      });
      return normalizeResponse(data);
    } catch (error) {
      handleError(error, 'listScheduledJobs');
    }
  },

  listFilteredJobs: async (
    params: {
      pageSize?: number;
      pageNumber?: number;
      status?: string;
      contactId?: string;
      resourceId?: string;
      typeId?: string;
    },
    signal?: AbortSignal
  ): Promise<JobList | undefined> => {
    try {
      const { data } = await api.get<JobList>('/jobFeed', {
        baseURL: JOB_DETAIL_V3_BASE_URL,
        params: {
          pageNumber: params.pageNumber ?? 1,
          pageSize: params.pageSize ?? 5,
          ...(params.status ? { status: params.status } : {}),
          ...(params.contactId ? { contactId: params.contactId } : {}),
          ...(params.resourceId ? { resourceId: params.resourceId } : {}),
          ...(params.typeId ? { typeId: params.typeId } : {}),
        },
        signal,
      });
      return normalizeResponse(data);
    } catch (error) {
      handleError(error, 'listFilteredJobs');
    }
  },

  getJob: async (
    jobId: string,
    signal?: AbortSignal
  ): Promise<JobData | undefined> => {
    try {
      const { data } = await api.get<JobData>('/modal/id', {
        baseURL: JOB_DETAIL_V3_BASE_URL,
        params: { jobId },
        signal,
        timeout: 30000,
      });
      return normalizeResponse(data);
    } catch (error) {
      handleError(error, 'getJob');
    }
  },

  getJobHistory: async (
    params: JobHistoryRequest,
    signal?: AbortSignal
  ): Promise<JobList | undefined> => {
    try {
      const { data } = await api.get<JobList>('/modal/history/contactId', {
        baseURL: API_BASE_URL,
        params,
        signal,
        timeout: 100000,
      });

      return normalizeResponse(data);
    } catch (error) {
      handleError(error, 'getJobHistory');
    }
  },

  getJobWorksheets: async (
    jobId: string,
    signal?: AbortSignal
  ): Promise<WorksheetResponse | undefined> => {
    try {
      const { data } = await api.get<WorksheetResponse>(
        `/modal/worksheets/${jobId}`,
        {
          baseURL: API_BASE_URL,
          signal,
          timeout: 30000,
        }
      );

      return normalizeResponse(data);
    } catch (error) {
      handleError(error, 'getJobWorksheets');
    }
  },
};
